import {UIComponent} from "@webfruits/core";

/******************************************************************
 * Tracking
 *
 * @author matthias.schulz@jash.de
 *****************************************************************/

export class Tracking {

    /******************************************************************
     * Constructor
     *****************************************************************/

    static init() {
        if (this.useGA && window['ga'] == null) {
            this.initGoogleAnalytics();
        }
        if (this.useGTAG && window['gtag'] == null) {
            this.initGoogleTagManager();
        }
        if (this.useMetaPixel && window['fbq'] == null) {
            this.initMetaPixel()
        }
        if (this.useClarity && window['clarity'] == null) {
            this.initMicrosoftClarity()
        }
        localStorage.setItem("tracking-accepted", "true");
    }

    static remove() {
        this.removeGoogleAnalytics()
        this.removeGoogleTagManager()
        this.removeMetaPixel()
        this.removeMicrosoftClarity()
        localStorage.setItem("tracking-accepted", "false");
    }

    /******************************************************************
     * Public Methodes
     *****************************************************************/

    static isTrackingAlreadyAccepted(): boolean {
        return localStorage.getItem("tracking-accepted") == "true";
    }

    static isAlreadyDeniedForThisSession() {
        return sessionStorage.getItem("tracking-denied") == "true";
    }

    static denyForThisSession() {
        sessionStorage.setItem("tracking-denied", "true");
    }

    static isAnyTrackingEnabledbyCMS(): boolean {
        return this.useMetaPixel || this.useClarity || this.useGA || this.useGTAG;
    }

    static get metaPixelID(): string {
        return this.headerElement.dataset.trackingMetapixelId
    }

    static get clarityID(): string {
        return this.headerElement.dataset.trackingClarityId
    }

    static get gaID(): string {
        return this.headerElement.dataset.trackingGaId
    }

    static get gtagID(): string {
        return this.headerElement.dataset.trackingGtagId
    }

    static get phoneNumber(): string {
        return this.headerElement.dataset.phoneNumber
    }

    static get useMetaPixel(): boolean {
        return this.metaPixelID?.length > 3
    }

    static get useClarity(): boolean {
        return this.clarityID?.length > 3
    }

    static get useGA(): boolean {
        return this.gaID?.length > 3
    }

    static get useGTAG(): boolean {
        return this.gtagID?.length > 3
    }

    static pushLayer(options: Object) {
        if (!window['dataLayer']) return;
        window['dataLayer'].push(options);
    }

    /******************************************************************
     * Private Methodes
     *****************************************************************/

    private static get headerElement(): HTMLElement {
        return document.body.getElementsByTagName("header")[0] as HTMLElement;
    }

    private static initGoogleAnalytics() {
        if (!this.useGA) return;
        window['ga'] = window['ga'] || function () {
            (window['ga'].q = window['ga'].q || []).push(arguments)
        };
        window['ga'].l = +new Date;
        window['ga']('create', this.gaID, 'auto');
        window['ga']('send', 'pageview');
        const script = new UIComponent<HTMLScriptElement>("script");
        script.view.async = true;
        script.view.src = `https://www.google-analytics.com/analytics.js`;
        document.body.appendChild(script.view)
    }

    private static removeGoogleAnalytics() {
        const gaScript = document.querySelector('script[src="https://www.google-analytics.com/analytics.js"]');
        if (gaScript && gaScript.parentNode) {
            gaScript.parentNode.removeChild(gaScript);
        }
        if (window['ga']) {
            delete window['ga'];
        }
        const cookies = document.cookie.split(";");
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            if (name === '_ga' || name === '_gid' || name.startsWith('_ga_')) {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname.replace(/^www\./, '')};`;
            }
        }
    }

    private static initGoogleTagManager() {
        if (!this.useGTAG) return;
        const script = new UIComponent<HTMLScriptElement>("script");
        script.view.async = true;
        script.view.src = `https://www.googletagmanager.com/gtag/js?id=${this.gtagID}`;
        window['dataLayer'] = window['dataLayer'] || [];
        window['gtag'] = () => {
            // @ts-ignore
            window['dataLayer'].push(arguments);
        }
        window['gtag']('js', new Date());
        window['gtag']('config', this.gtagID);
        document.body.appendChild(script.view)
    }

    private static removeGoogleTagManager() {
        const gtagScript = document.querySelector(`script[src^="https://www.googletagmanager.com/gtag/js"]`);
        if (gtagScript && gtagScript.parentNode) {
            gtagScript.parentNode.removeChild(gtagScript);
        }
        if (window['gtag']) {
            delete window['gtag'];
        }
        if (window['dataLayer']) {
            delete window['dataLayer'];
        }
        const cookies = document.cookie.split(";");
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
            if (name === '_ga' || name === '_gid' || name.startsWith('_ga_') || name.startsWith('_gcl_')) {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
                const domain = `.${window.location.hostname.replace(/^www\./, '')}`;
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain};`;
            }
        }
    }

    private static initMetaPixel() {
        if (!this.useMetaPixel) return;
        window['fbq'] = window['fbq'] || function () {
            if (window['fbq'].callMethod) {
                window['fbq'].callMethod.apply(window['fbq'], arguments);
            } else {
                window['fbq'].queue.push(arguments);
            }
        };
        if (!window['_fbq']) window['_fbq'] = window['fbq'];
        window['fbq'].push = window['fbq'];
        window['fbq'].loaded = true;
        window['fbq'].version = '2.0';
        window['fbq'].queue = [];
        const script = new UIComponent<HTMLScriptElement>("script");
        script.view.async = true;
        script.view.src = 'https://connect.facebook.net/en_US/fbevents.js';
        document.body.appendChild(script.view);
        window['fbq']('init', this.metaPixelID);
        window['fbq']('track', 'PageView');
        const noscript = document.createElement("noscript");
        const img = document.createElement("img");
        img.height = 1;
        img.width = 1;
        img.style.display = 'none';
        img.src = `https://www.facebook.com/tr?id=${this.metaPixelID}&ev=PageView&noscript=1`;
        noscript.appendChild(img);
        document.body.appendChild(noscript);
    }

    private static removeMetaPixel() {
        const fbScript = document.querySelector('script[src="https://connect.facebook.net/en_US/fbevents.js"]');
        if (fbScript && fbScript.parentNode) {
            fbScript.parentNode.removeChild(fbScript);
        }
        const fbNoscriptImg = document.querySelector(`noscript img[src*="facebook.com/tr?id=${this.metaPixelID}"]`);
        if (fbNoscriptImg) {
            const noscriptTag = fbNoscriptImg.parentNode;
            if (noscriptTag && noscriptTag.parentNode) {
                noscriptTag.parentNode.removeChild(noscriptTag);
            }
        }
        if (window['fbq']) {
            delete window['fbq'];
        }
        if (window['_fbq']) {
            delete window['_fbq'];
        }
        const cookies = document.cookie.split(";");
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
            if (name === '_fbp' || name === '_fbc') {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
                const domain = `.${window.location.hostname.replace(/^www\./, '')}`;
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain};`;
            }
        }
    }

    private static initMicrosoftClarity() {
        if (!this.useClarity) return;
        window['clarity'] = window['clarity'] || function () {
            (window['clarity'].q = window['clarity'].q || []).push(arguments);
        };
        const script = new UIComponent<HTMLScriptElement>("script");
        script.view.async = true;
        script.view.src = `https://www.clarity.ms/tag/${this.clarityID}`;
        document.body.appendChild(script.view);
    }

    private static removeMicrosoftClarity() {
        const clarityScript = document.querySelector(`script[src^="https://www.clarity.ms/tag/"]`);
        if (clarityScript && clarityScript.parentNode) {
            clarityScript.parentNode.removeChild(clarityScript);
        }
        if (window['clarity']) {
            delete window['clarity'];
        }
        try {
            const storageKeys = ['_clsk', '_cltk', '_clck'];
            storageKeys.forEach(key => {
                localStorage.removeItem(key);
                sessionStorage.removeItem(key);
            });
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith('_cl')) localStorage.removeItem(key);
            });
            Object.keys(sessionStorage).forEach(key => {
                if (key.startsWith('_cl')) sessionStorage.removeItem(key);
            });
        } catch (e) {
        }
        const cookies = document.cookie.split(";");
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            const eqPos = cookie.indexOf("=");
            const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
            if (name.startsWith('_cl')) {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
                const domain = `.${window.location.hostname.replace(/^www\./, '')}`;
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain};`;
            }
        }
    }

}
